import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, Router} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {DigitalCertificateService} from '@ccp/services/dc/digital-certificate.service';
import {NotificationService} from '@ccp/services/notifications/notification.service';
import {Logger} from '@ccp/lib/logger';
import {NotificationMessage} from '@ccp/localization/notifications/notification-message';
import {NotificationTypes} from '@ccp/enums/notification-types.enum';
import {DCDetails} from '@ccp/struct/dc/details.struct';
import {ShareableLink} from '@ccp/models/dc/shareable-link.model';
import {LocaleContextService} from '@ccp/services/app-context/locale-context.service';

interface ShareViewData {
  details?: DCDetails;
  expiredToken?: boolean;
  sharedLink?: ShareableLink;
  toHide?: boolean;
  isReadOnly?: boolean;
  uid?: string;
}

export const shareDataResolver: ResolveFn<Observable<ShareViewData>> = (route: ActivatedRouteSnapshot): Observable<ShareViewData> => {
  const dcService = inject(DigitalCertificateService);
  const notificationService = inject(NotificationService);
  const router = inject(Router);
  const localeContexteService = inject(LocaleContextService);

  const uid = route.paramMap.get('uid');
  const shareTokenUid = route.paramMap.get('token');

  if (shareTokenUid) {
    // Read-only mode
    return dcService
      .getCertificateByShareCode(
        shareTokenUid,
        localeContexteService.currentCountry$().isoCode,
        localeContexteService.currentLang$().isoCode,
      )
      .pipe(
        map((details) => ({details: details, toHide: true, isReadOnly: true, uid: uid})),
        catchError((error) => {
          notificationService.send(NotificationMessage['GENERIC_ERROR'], NotificationTypes.Error);
          Logger.error('Error loading read-only data:', error);
          return of({expiredToken: true, toHide: true, isReadOnly: true});
        }),
      );
  }
  // Standard share mode
  return dcService.createShareLink(uid).pipe(
    switchMap((sharedLink) =>
      dcService
        .getCertificateByShareCode(
          sharedLink.shareCode,
          localeContexteService.currentCountry$().isoCode,
          localeContexteService.currentLang$().isoCode,
        )
        .pipe(map((details) => ({sharedLink: sharedLink, details: details, uid: uid}))),
    ),
    catchError((error) => {
      notificationService.send(NotificationMessage['GENERIC_ERROR'], NotificationTypes.Error);
      Logger.error('Error loading share data:', error);
      // navigate to show page
      router.navigate(['certificate', uid, 'show']);
      return of(null);
    }),
  );
};
